import { StressLevel } from './gameStore';

export function generateRoomCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // No confusing chars
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

// Simple seeded random number generator for deterministic number generation
// This allows server-side validation of player number options
function seededRandom(seed: string): () => number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return function(): number {
    hash = (hash * 1103515245 + 12345) & 0x7fffffff;
    return hash / 0x7fffffff;
  };
}

// Generate strategic numbers when getting close to target (round >= 4)
// CRITICAL: Must use seeded random() everywhere, NOT Math.random()
function generateStrategicNumbers(
  random: () => number,
  currentTotal: number,
  targetTotal: number
): [number, number] {
  const distance = targetTotal - currentTotal;
  
  // Very close (within 10) - offer precise options
  if (distance > 0 && distance <= 10) {
    // One option could be the exact number needed or close to it
    const exactOrClose = random() < 0.3 ? distance : Math.max(1, distance - Math.floor(random() * 3));
    // Second option is slightly different - could be negative to add tension
    const secondOption = random() < 0.4 
      ? -Math.floor(random() * 3) - 1  // Small negative
      : Math.min(distance + 5, Math.floor(random() * 8) + 1); // Risky positive
    return [exactOrClose, secondOption];
  }
  
  // Getting closer (within 20) - mix of safe and risky
  if (distance > 0 && distance <= 20) {
    const safeOption = Math.floor(random() * Math.min(distance, 10)) + 1;
    // Risky option could overshoot or be negative
    const riskyOption = random() < 0.3
      ? -Math.floor(random() * 5) - 1
      : Math.floor(random() * 15) + 5;
    return [safeOption, riskyOption];
  }
  
  // Moderately close (within 35) - strategic options
  if (distance > 0 && distance <= 35) {
    const option1 = Math.floor(random() * 15) + 3;
    const option2 = random() < 0.25
      ? -Math.floor(random() * 5) - 1
      : Math.floor(random() * 20) + 5;
    return [option1, option2];
  }
  
  // Already overshot - recovery mode with negatives
  if (distance < 0) {
    const num1 = -Math.floor(random() * 10) - 1;
    const num2 = -Math.floor(random() * 10) - 1;
    return [num1, num2];
  }
  
  // Far from target - normal generation
  const num1 = Math.floor(random() * 25) + 5;
  const num2 = Math.floor(random() * 25) + 5;
  return [num1, num2];
}

export function generatePlayerNumbers(
  round: number, 
  isOvershoot: boolean, 
  seed?: string,
  currentTotal?: number,
  targetTotal: number = 67
): [number, number] {
  // Use seeded random if seed is provided (for deterministic generation)
  const random = seed ? seededRandom(seed) : Math.random;
  
  if (isOvershoot) {
    // Negative numbers for overshoot recovery
    const num1 = -Math.floor(random() * 10) - 1;
    const num2 = -Math.floor(random() * 10) - 1;
    return [num1, num2];
  }

  // After round 4, use strategic number generation if we have current total
  if (round >= 4 && currentTotal !== undefined) {
    return generateStrategicNumbers(random, currentTotal, targetTotal);
  }

  // Early rounds - standard generation
  let max = 20;
  if (round >= 7) max = 50;
  else if (round >= 4) max = 30;

  const num1 = Math.floor(random() * max) + 1;
  const num2 = Math.floor(random() * max) + 1;
  return [num1, num2];
}

// Generate a deterministic seed for server-side validation
export function generatePlayerNumberSeed(roomId: string, roundNumber: number, playerId: string): string {
  return `${roomId}-${roundNumber}-${playerId}`;
}

export function getBackgroundState(currentTotal: number, targetTotal: number): 'below' | 'above' | 'perfect' {
  if (currentTotal === targetTotal) return 'perfect';
  if (currentTotal < targetTotal) return 'below';
  return 'above';
}

export function calculateStressLevel(currentTotal: number, targetTotal: number): StressLevel {
  const diff = Math.abs(currentTotal - targetTotal);
  if (diff <= 2) return 'calm';
  if (diff <= 10) return 'nervous';
  return 'panic';
}

export function getStressEmoji(stress: StressLevel): string {
  switch (stress) {
    case 'calm': return '😊';
    case 'nervous': return '😰';
    case 'panic': return '😱';
  }
}

export function getBackgroundColor(state: 'below' | 'above' | 'perfect'): string {
  switch (state) {
    case 'below': return 'bg-chaos-red/20';
    case 'above': return 'bg-chaos-yellow/20';
    case 'perfect': return 'bg-chaos-green/20';
  }
}
